import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import validate from "validate.js";
import { showSnackbar } from "../../app/slices/commonSlice";
import {
  addLangResource,
  resetStateLanguages,
} from "../../app/slices/languageSlice";
import { capitalizeWord, hasAccess, getObjectDifference } from "../../app/utils";

const LanguageDialog = ({ open, handleClose, selectedLangResource = null }) => {
  const {
    post: { data, status, error },
  } = useSelector(({ languages }) => languages);

  const dispatch = useDispatch();

  const [isReadOnly] = useState(
    !hasAccess({ type: "name", resource: "MANAGE LANGUAGES" }),
  );

  const [formValues, setFormValues] = useState({
    key: selectedLangResource?.key || "",
    english: selectedLangResource?.en || "",
    swahili: selectedLangResource?.sw || "",
    status: selectedLangResource?.status || "active",
  });

  const [errors, setErrors] = useState({});
  const statuses = ["active", "inactive", "disabled"];

  const hasChanges = useMemo(() => {
    if (!selectedLangResource) return true;
    const initialPayload = {
      key: selectedLangResource?.key || "",
      english: selectedLangResource?.en || "",
      swahili: selectedLangResource?.sw || "",
      status: selectedLangResource?.status || "active",
    };
    return Object.keys(getObjectDifference(initialPayload, formValues)).length > 0;
  }, [formValues, selectedLangResource]);

  const validateSchema = {
    key: { presence: { allowEmpty: false } },
    english: { presence: { allowEmpty: false } },
    swahili: { presence: { allowEmpty: false } },
  };

  useEffect(() => {
    if (data) {
      dispatch(
        showSnackbar({
          open: true,
          message: "Resource list updated successfully",
          type: "success",
        }),
      );

      dispatch(resetStateLanguages(["post"]));
      handleClose(true);
    }
  }, [data, dispatch, handleClose]);

  useEffect(() => {
    if (error) {
      dispatch(
        showSnackbar({
          open: true,
          message: error?.message || "An error occurred",
          type: "error",
          timeout: 3000,
        }),
      );

      dispatch(resetStateLanguages(["post"]));
    }
  }, [error, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (selectedLangResource?.status === "disabled") {
      dispatch(
        showSnackbar({
          open: true,
          message: "Resource is disabled. Update is not allowed",
          type: "error",
        }),
      );
      return;
    }

    if (selectedLangResource && !hasChanges) {
      dispatch(
        showSnackbar({
          open: true,
          message: "No changes to submit",
          type: "info",
        }),
      );
      return;
    }

    const validationErrors = validate(formValues, validateSchema);

    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    const payload = {
      key: formValues.key,
      en: formValues.english,
      sw: formValues.swahili,
      status: formValues.status,
    };

    if (selectedLangResource) {
      const initialPayload = {
        key: selectedLangResource?.key || "",
        en: selectedLangResource?.en || "",
        sw: selectedLangResource?.sw || "",
        status: selectedLangResource?.status || "active",
      };
      const diff = getObjectDifference(initialPayload, payload);
      diff.key = selectedLangResource.key; // preserve identifier
      dispatch(addLangResource(diff));
    } else {
      dispatch(addLangResource(payload));
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      onClose={() => handleClose(false)}
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {selectedLangResource ? "Update" : "Add"} Resource
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                label="Resource Key"
                name="key"
                value={formValues.key}
                onChange={handleChange}
                disabled={selectedLangResource !== null || isReadOnly}
                error={Boolean(errors.key)}
                helperText={errors.key?.[0] || ""}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                multiline
                label="English"
                name="english"
                value={formValues.english}
                onChange={handleChange}
                disabled={isReadOnly}
                error={Boolean(errors.english)}
                helperText={errors.english?.[0] || ""}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                multiline
                label="Swahili"
                name="swahili"
                value={formValues.swahili}
                onChange={handleChange}
                disabled={isReadOnly}
                error={Boolean(errors.swahili)}
                helperText={errors.swahili?.[0] || ""}
              />
            </Grid>

            {selectedLangResource && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Resource Status"
                  name="status"
                  value={formValues.status}
                  onChange={handleChange}
                  disabled={isReadOnly}
                >
                  {statuses.map((option) => (
                    <MenuItem key={option} value={option}>
                      {capitalizeWord(option)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>

          <DialogActions sx={{ mt: 3, px: 0 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={status === "loading" || isReadOnly || !hasChanges}
            >
              {status === "loading" && (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              )}
              {selectedLangResource ? "Update" : "Add"}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={() => handleClose(false)}
            >
              Close
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LanguageDialog;
